import {
  createContext,
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { IDashBoardPanel } from "../../models/dashboardpanel";

export type TDashboardViewMode = "list" | "grid";

interface IDashBoardViewContextProps {
  dashboards: IDashBoardPanel[];
  viewMode: TDashboardViewMode;
  isView: boolean;
  onChangeViewMode: (mode: TDashboardViewMode) => void;
  setDashboards: Dispatch<SetStateAction<IDashBoardPanel[]>>;
}

const DashBoardViewContext = createContext<
  IDashBoardViewContextProps | undefined
>(undefined);

interface IDashBoardViewProviderProps {
  children: React.ReactNode;
  isView: boolean;
}

export const DashBoardViewProvider: React.FC<IDashBoardViewProviderProps> =
  memo(({ children, isView }) => {
    const [viewMode, setViewMode] = useState<TDashboardViewMode>(
      (localStorage.getItem("viewMode") as TDashboardViewMode) || "grid"
    );

    const [dashboards, setDashboards] = useState<IDashBoardPanel[]>([]);

    const onChangeViewMode = useCallback((mode: TDashboardViewMode) => {
      localStorage.setItem("viewMode", mode);
      setViewMode(mode);
    }, []);

    const value = useMemo(
      () => ({
        dashboards,
        viewMode,
        isView,
        onChangeViewMode,
        setDashboards,
      }),
      [dashboards, viewMode, isView, onChangeViewMode, setDashboards]
    );

    return (
      <DashBoardViewContext.Provider value={value}>
        {children}
      </DashBoardViewContext.Provider>
    );
  });

export const useDashboardView = () => {
  const context = useContext(DashBoardViewContext);

  if (!context) {
    throw new Error("useDashboardView must be used with DashBoardViewProvider");
  }

  return context;
};
